import * as React from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Menu, MenuItem, TableCell, styled, Typography } from '@mui/material';
import Chip, { ChipStatus } from 'src/frontend-common/components/Chip';
import { UserDetail, UserRole } from 'src/dto/User';
import { capitalize } from 'src/frontend-common/utils/functions';
import Column from 'src/frontend-common/components/Column';
import { getRoleLabel } from 'src/utils/user';
import { paths } from 'src/frontend-common/components/Router';
import Row from 'src/frontend-common/components/Row';
import { Link, useNavigate } from 'react-router-dom';
import ButtonComponent from 'src/components/ButtonComponent';
import { useGetLoginHistoryQuery } from 'src/redux/services/users';
import Ellipsis from 'src/frontend-common/components/Ellipsis';

export interface Props {
  user: UserDetail;
  currentUserIsAdmin: boolean;
  teamVersion?: boolean;
}

const CustomButton = styled(Button)({
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const RoleChip = styled('span')(({ theme }) => ({
  border: '1px solid #E0E0E0',
  borderRadius: '20px',
  padding: theme.spacing(1, 2),
}));

const TypoBold = styled(Typography)(() => ({
  fontWeight: 500,
}));

const ColumnNameAndEmail = styled(Column)(({ theme }) => ({
  justifyContent: 'center',
  paddingLeft: theme.spacing(1),
}));

const statusToChipVariant = (status: boolean | undefined): ChipStatus => {
  return status ? 'success' : 'disabled';
};

const printUserRoles = (roles: UserRole[]) => {
  let output = '';
  roles.forEach((role: UserRole) => {
    if (role !== 'ROLE_USER') {
      output = getRoleLabel(role);
    }
  });
  return output;
};
const InvisibleLink = styled(Link)({
  textDecoration: 'none',
  color: 'inherit',
});

export const UserCells: React.FC<Props> = ({ user, currentUserIsAdmin, teamVersion }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleOpen = (event: { currentTarget: React.SetStateAction<HTMLElement | null> }) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const textAlignRole = teamVersion ? 'left!important' : 'inherit';

  let userRole = <span>{printUserRoles(user.roles)}</span>;
  if (teamVersion) {
    const teamRole = printUserRoles(user.roles) === 'Customer' ? 'User' : 'Admin';
    userRole = <RoleChip>{teamRole}</RoleChip>;
  }
  const { data: loginUserInfoData } = useGetLoginHistoryQuery({
    username: user?.email || '',
  });

  const getUserLabel = (user: UserDetail): string => {
    if (user.first_name && user.last_name) {
      return `${capitalize(user.first_name)} ${capitalize(user.last_name)}`;
    }

    if (user.first_name) {
      return capitalize(user.first_name);
    }

    if (user.last_name) {
      return capitalize(user.last_name);
    }

    if (user.phone) {
      return user.phone.toLowerCase();
    }

    if (user.email) {
      return user.email.toLowerCase();
    }

    return ' - ';
  };

  return (
    <>
      <TableCell>
        <Row>
          <ColumnNameAndEmail>
            <>
              {currentUserIsAdmin && (
                <InvisibleLink style={{ cursor: 'pointer' }} to={`${paths.userDetail.prePath}/${user.id}/` as const}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {getUserLabel(user)}
                  </Typography>
                </InvisibleLink>
              )}
            </>
            <>
              {!currentUserIsAdmin && (
                <TypoBold variant="body2" data-testid="user-cell-label-text">
                  {getUserLabel(user)}
                </TypoBold>
              )}
            </>
            <Ellipsis variant="body2" color="text.secondary">
              {user?.email.toLowerCase()}
            </Ellipsis>
          </ColumnNameAndEmail>
        </Row>
      </TableCell>

      {!teamVersion && (
        <TableCell>
          <Chip status={statusToChipVariant(user.active)}>{user.active ? 'Active' : 'Inactive'}</Chip>
        </TableCell>
      )}

      <TableCell sx={{ textAlign: textAlignRole }}>{userRole}</TableCell>

      {!teamVersion && <TableCell sx={{ textAlign: textAlignRole }}>{loginUserInfoData?.logins?.[0]?.date ?? '-'}</TableCell>}

      {!teamVersion && (
        <TableCell>
          <ButtonComponent icon={<MoreHorizIcon />} buttontype="icon" onClick={handleOpen} />

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem onClick={() => navigate(`${paths.userDetail.prePath}/${user.id}`)}>
              <CustomButton variant="text" startIcon={<EditIcon />} disableRipple color="inherit">
                Edit
              </CustomButton>
            </MenuItem>
          </Menu>
        </TableCell>
      )}
    </>
  );
};
